import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface InventoryItem {
  id: string;
  product: string;
  sku: string;
  stock: number;
  reserved: number;
  available: number;
  reorderPoint: number;
  location: string;
  lastUpdated: string;
}

const MOCK_INVENTORY: InventoryItem[] = [
  { id: "1", product: "Premium Headphones", sku: "ELEC-001", stock: 45, reserved: 5, available: 40, reorderPoint: 20, location: "Warehouse A", lastUpdated: "2024-01-15" },
  { id: "2", product: "Wireless Mouse", sku: "ELEC-002", stock: 120, reserved: 10, available: 110, reorderPoint: 30, location: "Warehouse A", lastUpdated: "2024-01-14" },
  { id: "3", product: "USB-C Cable", sku: "ELEC-003", stock: 8, reserved: 0, available: 8, reorderPoint: 50, location: "Warehouse B", lastUpdated: "2024-01-10" },
  { id: "4", product: "Cotton T-Shirt", sku: "CLTH-001", stock: 85, reserved: 15, available: 70, reorderPoint: 25, location: "Store Front", lastUpdated: "2024-01-15" },
  { id: "5", product: "Denim Jeans", sku: "CLTH-002", stock: 32, reserved: 2, available: 30, reorderPoint: 15, location: "Store Front", lastUpdated: "2024-01-13" },
  { id: "6", product: "Running Shoes", sku: "CLTH-003", stock: 18, reserved: 0, available: 18, reorderPoint: 20, location: "Warehouse A", lastUpdated: "2024-01-12" },
];

export default function StockSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const filteredInventory = MOCK_INVENTORY.filter((item) =>
    item.product.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const lowStockItems = MOCK_INVENTORY.filter((item) => item.available <= item.reorderPoint);
  const totalItems = MOCK_INVENTORY.reduce((acc, item) => acc + item.stock, 0);
  const totalValue = 125000;

  const getStockStatus = (item: InventoryItem) => {
    if (item.available === 0) {
      return { color: "text-red-500 bg-red-500/20", label: t("posInventory.outOfStock") };
    }
    if (item.available <= item.reorderPoint) {
      return { color: "text-amber-500 bg-amber-500/20", label: t("posInventory.lowStock") };
    }
    return { color: "text-emerald-500 bg-emerald-500/20", label: t("posInventory.inStock") };
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posInventory.title")}</h1>
          <p className={themeClasses.textSecondary}>{t("posInventory.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium flex items-center gap-2`}>
            <i className="ri-download-2-line"></i>
            {t("posInventory.export")}
          </button>
          <button className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center gap-2">
            <i className="ri-add-line"></i>
            {t("posInventory.adjustment")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.totalItems")}</span>
            <i className="ri-stack-line text-blue-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{totalItems.toLocaleString()}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.totalValue")}</span>
            <i className="ri-money-dollar-circle-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>${totalValue.toLocaleString()}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.lowStockAlerts")}</span>
            <i className="ri-alarm-warning-line text-amber-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-amber-500`}>{lowStockItems.length}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posInventory.locations")}</span>
            <i className="ri-map-pin-line text-violet-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>3</p>
        </div>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`p-4 border-b ${themeClasses.border} flex flex-col sm:flex-row gap-4`}>
          <div className="flex-1 relative">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            <input
              type="text"
              placeholder={t("posInventory.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>

          <div className="flex gap-2">
            <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} flex items-center gap-2`}>
              <i className="ri-filter-line"></i>
              {t("posInventory.filters")}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${themeClasses.cardBg} border-b ${themeClasses.border}`}>
              <tr>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.product")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.onHand")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.reserved")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.available")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.location")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.status")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posInventory.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredInventory.map((item) => {
                const status = getStockStatus(item);
                return (
                  <tr key={item.id} className={`border-b ${themeClasses.border} ${themeClasses.tableRowHover}`}>
                    <td className="px-4 py-3">
                      <div>
                        <p className={`font-medium ${themeClasses.text}`}>{item.product}</p>
                        <p className={`text-sm ${themeClasses.textSecondary}`}>{item.sku}</p>
                      </div>
                    </td>
                    <td className={`px-4 py-3 text-right font-medium ${themeClasses.text}`}>{item.stock}</td>
                    <td className={`px-4 py-3 text-right ${themeClasses.textSecondary}`}>{item.reserved}</td>
                    <td className={`px-4 py-3 text-right font-bold ${item.available <= item.reorderPoint ? "text-amber-500" : themeClasses.text}`}>
                      {item.available}
                    </td>
                    <td className={`px-4 py-3 ${themeClasses.textSecondary}`}>{item.location}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                          <i className="ri-history-line"></i>
                        </button>
                        <button className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                          <i className="ri-equalizer-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
